import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Circle, Waves, GitBranch, User, Heart, Users, Baby, ArrowRight } from 'lucide-react';
export function Decode() {
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Decode',
    description: "The Human Pattern Engine - reveals emotional patterns behind how you think, react, and relate. Human behaviour isn't random — it runs on patterns.",
    brand: {
      '@type': 'Brand',
      name: 'Lazy Monk Labs'
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
      url: 'https://lazymonklabs.com/decode',
      priceCurrency: 'USD'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '12'
    }
  };
  return <>
      <Helmet data-id="element-10">
        <title data-id="element-11">Decode — The Human Pattern Engine | Lazy Monk Labs</title>
        <meta name="description" content="Decode reveals the emotional patterns behind how you think, react, and relate. Human behaviour isn't random — it runs on patterns. Join the waitlist." data-id="element-12" />
        <meta name="keywords" content="Decode, human pattern engine, emotional patterns, behavioural loops, relationship dynamics, self-awareness, consciousness engineering, emotional architecture" data-id="element-13" />
        <meta name="robots" content="index, follow" data-id="element-14" />
        <meta name="author" content="Lazy Monk Labs" data-id="element-15" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Decode — The Human Pattern Engine | Lazy Monk Labs" data-id="element-16" />
        <meta property="og:description" content="Decode reveals the emotional patterns behind how you think, react, and relate. Human behaviour isn't random — it runs on patterns." data-id="element-17" />
        <meta property="og:url" content="https://lazymonklabs.com/decode" data-id="element-18" />
        <meta property="og:type" content="product" data-id="element-19" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/gqbXRsBy5a8YkBwGLByGB5/ChatGPT_Image_Nov_19%2C_2025%2C_08_41_32_AM.png" data-id="element-20" />
        <meta property="og:site_name" content="Lazy Monk Labs" data-id="element-21" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-22" />
        <meta name="twitter:title" content="Decode — The Human Pattern Engine" data-id="element-23" />
        <meta name="twitter:description" content="See the architecture behind your mind and your relationships. Decode uncovers the hidden patterns that shape your life." data-id="element-24" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/gqbXRsBy5a8YkBwGLByGB5/ChatGPT_Image_Nov_19%2C_2025%2C_08_41_32_AM.png" data-id="element-25" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://lazymonklabs.com/decode" data-id="element-26" />

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-27"></script>
        <script data-id="element-28">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>

        {/* Structured Data */}
        <script type="application/ld+json" data-id="element-29">
          {JSON.stringify(productStructuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-[#FAFAFA] text-[#2D2D2D] relative overflow-hidden" data-id="element-30">
        {/* Navigation */}
        <header data-id="element-31">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAFA]/95 backdrop-blur-sm border-b border-[#2D2D2D]/10" role="navigation" aria-label="Main navigation" data-id="element-32">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-33">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-34">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs - Consciousness Engineering Logo" className="h-8 w-auto" width="32" height="32" data-id="element-35" />
                <span className="text-lg font-medium tracking-tight" data-id="element-36">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-37">
                <Link to="/decode" className="text-sm text-[#2D2D2D]/80 hover:text-[#2D2D2D] transition-colors" aria-current="page" data-id="element-38">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#2D2D2D]/80 hover:text-[#2D2D2D] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-39">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Subtle Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-40">
          <svg className="absolute top-20 left-10 w-96 h-96 opacity-[0.03]" viewBox="0 0 200 200" data-id="element-41">
            <circle cx="100" cy="100" r="80" stroke="#2D2D2D" strokeWidth="0.5" fill="none" data-id="element-42" />
            <circle cx="100" cy="100" r="60" stroke="#2D2D2D" strokeWidth="0.5" fill="none" data-id="element-43" />
          </svg>
          <svg className="absolute top-1/3 right-0 w-80 h-80 opacity-[0.03]" viewBox="0 0 200 200" data-id="element-44">
            <path d="M 20 100 Q 60 80, 100 100 T 180 100" stroke="#2D2D2D" strokeWidth="0.5" fill="none" data-id="element-45" />
            <path d="M 20 120 Q 60 100, 100 120 T 180 120" stroke="#2D2D2D" strokeWidth="0.5" fill="none" data-id="element-46" />
          </svg>
          <svg className="absolute bottom-1/4 left-1/4 w-72 h-72 opacity-[0.03]" viewBox="0 0 200 200" data-id="element-47">
            <circle cx="100" cy="100" r="70" stroke="#9EAAB4" strokeWidth="0.5" fill="none" data-id="element-48" />
            <circle cx="130" cy="100" r="70" stroke="#9EAAB4" strokeWidth="0.5" fill="none" data-id="element-49" />
          </svg>
        </div>

        {/* Hero Section */}
        <section className="px-6 py-24 md:py-40 max-w-4xl mx-auto text-center relative" aria-labelledby="decode-hero-heading" data-id="element-50">
          <h1 id="decode-hero-heading" className="text-4xl md:text-6xl font-medium mb-8 leading-tight tracking-wide" data-id="element-51">
            Decode — The Human Pattern Engine
          </h1>
          <p className="text-lg md:text-xl text-[#2D2D2D]/70 mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-52">
            Reveal the emotional patterns behind your emotions and
            relationships. Human behaviour isn't random — it runs on patterns.
          </p>
          <a href="https://airtable.com/appuDbbb9QikRS6Z4/pagQncAoSc8Jlla2p/form" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2D2D2D] text-white font-medium rounded-lg hover:bg-[#2D2D2D]/90 transition-colors shadow-md hover:shadow-lg" aria-label="Join Decode waitlist" data-id="element-53">
            Join Waitlist
            <ArrowRight size={18} strokeWidth={2} aria-hidden="true" data-id="element-54" />
          </a>

          {/* Decoder Visual */}
          <div className="mt-16 max-w-3xl mx-auto" data-id="element-55">
            <div className="rounded-lg border border-[#2D2D2D]/10 shadow-lg bg-white p-8 md:p-12" data-id="element-56">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/57vFdjYG2E5PigDok1Hxm6/ChatGPT_Image_Nov_18%2C_2025%2C_09_52_53_PM.png" alt="Decode pattern mapping visualization showing emotional architecture and behavioral patterns" className="w-full h-auto rounded-lg" loading="eager" data-id="element-57" />
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" aria-hidden="true" data-id="element-58">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2D2D2D]/10 to-transparent" data-id="element-59"></div>
        </div>

        {/* Value Section */}
        <section className="px-6 py-20 md:py-32 max-w-3xl mx-auto relative" aria-labelledby="value-heading" data-id="element-60">
          <h2 id="value-heading" className="text-2xl md:text-3xl font-medium mb-12 text-center tracking-wide" data-id="element-61">
            The hidden architecture of human behaviour
          </h2>
          <ul className="space-y-6 text-base md:text-lg text-[#2D2D2D]/70" aria-label="Decode capabilities" data-id="element-62">
            <li className="flex items-start gap-4" data-id="element-63">
              <Circle size={20} className="text-[#9EAAB4] mt-1 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-64" />
              <span data-id="element-65">Map your emotional patterns and behavioural loops</span>
            </li>
            <li className="flex items-start gap-4" data-id="element-66">
              <Waves size={20} className="text-[#9EAAB4] mt-1 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-67" />
              <span data-id="element-68">
                Understand relationship dynamics and conflict patterns
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-69">
              <GitBranch size={20} className="text-[#9EAAB4] mt-1 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-70" />
              <span data-id="element-71">Predict behaviour with precision and clarity</span>
            </li>
          </ul>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" aria-hidden="true" data-id="element-72">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2D2D2D]/10 to-transparent" data-id="element-73"></div>
        </div>

        {/* Clarity Section */}
        <section className="px-6 py-20 md:py-32 max-w-3xl mx-auto relative" aria-labelledby="precision-heading" data-id="element-74">
          <h3 id="precision-heading" className="text-xl md:text-2xl font-medium mb-8 text-center tracking-wide" data-id="element-75">
            A precision system for human behaviour
          </h3>
          <p className="text-base md:text-lg text-[#2D2D2D]/70 leading-relaxed text-center" data-id="element-76">
            Decode reveals the underlying architecture of your mind, emotions,
            and relationships. Fast. Precise. Predictable.
          </p>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" aria-hidden="true" data-id="element-77">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2D2D2D]/10 to-transparent" data-id="element-78"></div>
        </div>

        {/* Who Decode Is For Section */}
        <section className="px-6 py-20 md:py-32 max-w-3xl mx-auto relative" aria-labelledby="who-for-heading" data-id="element-79">
          <h2 id="who-for-heading" className="text-2xl md:text-3xl font-medium mb-12 text-center tracking-wide" data-id="element-80">
            Who Decode Is For
          </h2>
          <div className="space-y-6" data-id="element-81">
            <article className="p-6 rounded-lg border border-[#2D2D2D]/5 shadow-sm hover:shadow-md transition-shadow" data-id="element-82">
              <div className="flex items-start gap-4" data-id="element-83">
                <User size={22} className="text-[#9EAAB4] mt-0.5 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-84" />
                <div data-id="element-85">
                  <h3 className="text-lg font-medium mb-2" data-id="element-86">For Individuals</h3>
                  <p className="text-base text-[#2D2D2D]/70 leading-relaxed" data-id="element-87">
                    Map your emotional wiring and reaction patterns to
                    understand yourself better.
                  </p>
                </div>
              </div>
            </article>
            <article className="p-6 rounded-lg border border-[#2D2D2D]/5 shadow-sm hover:shadow-md transition-shadow" data-id="element-88">
              <div className="flex items-start gap-4" data-id="element-89">
                <Heart size={22} className="text-[#9EAAB4] mt-0.5 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-90" />
                <div data-id="element-91">
                  <h3 className="text-lg font-medium mb-2" data-id="element-92">
                    For Relationships
                  </h3>
                  <p className="text-base text-[#2D2D2D]/70 leading-relaxed" data-id="element-93">
                    Decode attraction, conflict, and recurring relationship
                    dynamics.
                  </p>
                </div>
              </div>
            </article>
            <article className="p-6 rounded-lg border border-[#2D2D2D]/5 shadow-sm hover:shadow-md transition-shadow" data-id="element-94">
              <div className="flex items-start gap-4" data-id="element-95">
                <Users size={22} className="text-[#9EAAB4] mt-0.5 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-96" />
                <div data-id="element-97">
                  <h3 className="text-lg font-medium mb-2" data-id="element-98">
                    For Bosses, Colleagues & Teams
                  </h3>
                  <p className="text-base text-[#2D2D2D]/70 leading-relaxed" data-id="element-99">
                    Understand workplace behaviour and conflict patterns in team
                    dynamics.
                  </p>
                </div>
              </div>
            </article>
            <article className="p-6 rounded-lg border border-[#2D2D2D]/5 shadow-sm hover:shadow-md transition-shadow" data-id="element-100">
              <div className="flex items-start gap-4" data-id="element-101">
                <Baby size={22} className="text-[#9EAAB4] mt-0.5 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-102" />
                <div data-id="element-103">
                  <h3 className="text-lg font-medium mb-2" data-id="element-104">For Parents</h3>
                  <p className="text-base text-[#2D2D2D]/70 leading-relaxed" data-id="element-105">
                    Decode your child's emotional patterns and triggers for
                    better parenting.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" aria-hidden="true" data-id="element-106">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2D2D2D]/10 to-transparent" data-id="element-107"></div>
        </div>

        {/* What People Are Saying Section */}
        <section className="px-6 py-20 md:py-32 max-w-4xl mx-auto relative" aria-labelledby="testimonials-heading" data-id="element-108">
          <h2 id="testimonials-heading" className="text-2xl md:text-3xl font-medium mb-12 text-center tracking-wide" data-id="element-109">
            Early reactions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-110">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/md64xZJJxSoMgdrFhrVpxJ/Screenshot_2025-11-18_at_11.27.44.png" alt="User testimonial: Decode helped me understand my emotional patterns" className="w-full rounded-lg border border-[#2D2D2D]/5 shadow-sm" loading="lazy" data-id="element-111" />
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jaKcXtbZ8sYHAZ6qjAsr2j/Screenshot_2025-11-18_at_11.28.15.png" alt="User testimonial: Decode revealed relationship dynamics I couldn't see" className="w-full rounded-lg border border-[#2D2D2D]/5 shadow-sm" loading="lazy" data-id="element-112" />
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tKhCTmFgMf1hLy5X3TuNAd/Screenshot_2025-11-18_at_11.32.07.png" alt="User testimonial: Decode gave me clarity on my behavioural loops" className="w-full rounded-lg border border-[#2D2D2D]/5 shadow-sm" loading="lazy" data-id="element-113" />
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gcH9GQnGVjTXVVHcxzTL77/Screenshot_2025-11-18_at_11.46.16.png" alt="User testimonial: Decode is a game-changer for self-awareness" className="w-full rounded-lg border border-[#2D2D2D]/5 shadow-sm" loading="lazy" data-id="element-114" />
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4X2Bepb4PJAdX3pxs2PYVr/Screenshot_2025-11-18_at_12.03.18.png" alt="User testimonial: Decode helped me understand my inner architecture" className="w-full rounded-lg border border-[#2D2D2D]/5 shadow-sm md:col-span-2" loading="lazy" data-id="element-115" />
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6" aria-hidden="true" data-id="element-116">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2D2D2D]/10 to-transparent" data-id="element-117"></div>
        </div>

        {/* Waitlist CTA Section */}
        <section className="px-6 py-20 md:py-32 max-w-3xl mx-auto text-center relative" aria-labelledby="cta-heading" data-id="element-118">
          <p id="cta-heading" className="text-lg md:text-xl text-[#2D2D2D]/70 mb-10" data-id="element-119">
            Get early access to Decode
          </p>
          <a href="https://airtable.com/appuDbbb9QikRS6Z4/pagQncAoSc8Jlla2p/form" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2D2D2D] text-white font-medium rounded-lg hover:bg-[#2D2D2D]/90 transition-colors shadow-md hover:shadow-lg" aria-label="Join Decode waitlist for early access" data-id="element-120">
            Join Waitlist
            <ArrowRight size={18} strokeWidth={2} aria-hidden="true" data-id="element-121" />
          </a>
        </section>

        {/* Footer */}
        <footer className="px-6 py-16 text-center relative border-t border-[#2D2D2D]/10" role="contentinfo" data-id="element-122">
          <div className="max-w-3xl mx-auto" data-id="element-123">
            <p className="text-sm text-[#2D2D2D]/70 mb-4" data-id="element-124">
              A product by{' '}
              <Link to="/" className="underline hover:text-[#2D2D2D]" data-id="element-125">
                Lazy Monk Labs
              </Link>{' '}
              — consciousness engineering for humans.
            </p>
            <p className="text-sm text-[#2D2D2D]/50 mb-2 tracking-wide" data-id="element-126">
              Truth, engineered.
            </p>
            <p className="text-xs text-[#2D2D2D]/40" data-id="element-127">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </>;
}